/**
 * Application protocol that travels ENCRYPTED inside the content of the 1:1
 * double ratchet. The relay never sees it in the clear. Covers: identity
 * presentation (for the safety number), delivery receipts, group invite,
 * sender-key distribution and group messages (star topology with the hub creator).
 */

import type { GroupMessage, SenderKeyDistribution } from "@susurro/protocol";

import type { CallSignal } from "./call";
import type { MediaKind } from "./media";
import type { P2pSignal } from "./p2p";

export interface HelloControl {
  readonly t: "hello";
  /** Sender's public identity keys (base64) for the safety number. */
  readonly idDhPub: string;
  readonly idSigPub: string;
}

export interface ReceiptControl {
  readonly t: "receipt";
  readonly id: string;
}

export interface GroupInviteControl {
  readonly t: "group-invite";
  readonly groupId: string;
  readonly title: string;
  readonly members: string[];
  readonly skd: SenderKeyDistribution;
}

export interface SenderKeyControl {
  readonly t: "skd";
  readonly groupId: string;
  /** Device owning this sender-key (preserved when the hub forwards it). */
  readonly sender: string;
  readonly skd: SenderKeyDistribution;
}

export interface GroupMessageControl {
  readonly t: "gmsg";
  readonly groupId: string;
  readonly msgId: string;
  readonly authorDevice: string;
  readonly sentAt: number;
  readonly expiresInSecs?: number;
  readonly gm: GroupMessage;
}

/**
 * Announcement of an E2E ATTACHMENT (file / voice note / photo / video). It carries the
 * content key (base64) and the metadata; the encrypted bytes arrive separately
 * in `attach-chunk`. Everything travels ENCRYPTED inside the 1:1 double ratchet → the relay
 * only sees the opaque ciphertext of the envelope (neither the plaintext nor the key).
 */
export interface AttachControl {
  readonly t: "attach";
  readonly msgId: string;
  readonly attachId: string;
  readonly name: string;
  readonly mime: string;
  readonly mediaKind: MediaKind;
  readonly size: number;
  readonly sha256: string;
  /** AES-256-GCM content key (base64). */
  readonly keyB64: string;
  readonly chunkCount: number;
  readonly sentAt: number;
  readonly caption?: string;
  readonly expiresInSecs?: number;
  readonly durationMs?: number;
  readonly width?: number;
  readonly height?: number;
}

/** An encrypted chunk of the attachment (`base64(iv||ciphertext)`). */
export interface AttachChunkControl {
  readonly t: "attach-chunk";
  readonly attachId: string;
  readonly index: number;
  readonly data: string;
}

/**
 * Internal GROUP control encrypted by sender-key: `gm` decrypts (with the
 * `authorDevice` chain) into the JSON of an internal control (`attach` / `attach-chunk`).
 * It is the same path as `gmsg` but for media: 1 encryption per message, the hub
 * forwards in a star and the relay only sees the opaque ratcheted envelope.
 */
export interface GroupControlControl {
  readonly t: "gctl";
  readonly groupId: string;
  readonly authorDevice: string;
  readonly gm: GroupMessage;
}

/**
 * Multi-device echo: what I send from one device is replicated to my
 * other linked devices as an OUTGOING message (the conversation stays
 * consistent everywhere). It is only accepted from a verified sibling device.
 */
export interface EchoControl {
  readonly t: "echo";
  /** Peer of the 1:1 conversation the message belongs to. */
  readonly peer: string;
  readonly msgId: string;
  readonly text: string;
  readonly sentAt: number;
  readonly expiresInSecs?: number;
}

/**
 * Signaling of a voice/video CALL (SDP offer/answer + ICE + hangup).
 * It travels ENCRYPTED inside the 1:1 double ratchet → the relay only sees the opaque
 * ciphertext of the envelope: never the SDP nor the DTLS fingerprint. The media
 * (DTLS-SRTP) flows P2P over WebRTC, verified by the identity keys (same X3DH channel).
 */
export interface CallControl {
  readonly t: "call";
  readonly signal: CallSignal;
}

/**
 * Negotiation of the direct P2P data channel (P2P-first transport). Same as
 * calls: the signal (SDP offer/answer + ICE) travels ENCRYPTED through the
 * 1:1 double ratchet → the relay never sees the SDP nor the DTLS fingerprint. Once
 * the DataChannel is open, the envelopes travel directly and the relay does not even take part.
 */
export interface P2pControl {
  readonly t: "p2p";
  readonly signal: P2pSignal;
}

/** Serializable conversation for the multi-device handover (keys in base64). */
export interface LinkConversation {
  readonly id: string;
  readonly kind: "direct" | "group";
  readonly title: string;
  readonly peerDeviceId?: string;
  readonly groupId?: string;
  readonly memberDeviceIds: string[];
  readonly createdAt: number;
  readonly lastActivityAt: number;
  readonly peerIdentityDhPub?: string;
  readonly peerIdentitySigPub?: string;
}

/** Serializable message (already plain: no Uint8Array) for the handover. */
export interface LinkMessage {
  readonly id: string;
  readonly conversationId: string;
  readonly direction: "out" | "in";
  readonly authorDevice: string;
  readonly text: string;
  readonly sentAt: number;
  readonly expiresInSecs?: number;
}

/**
 * Snapshot of conversations+messages that the primary device SENDS to a
 * new device to link it (recover the history on another device).
 * It travels ENCRYPTED inside the 1:1 double ratchet → the relay only sees an opaque blob.
 */
export interface LinkSnapshotControl {
  readonly t: "link-snapshot";
  readonly conversations: LinkConversation[];
  readonly messages: LinkMessage[];
  /** Full set of devices of this identity (includes the primary one). */
  readonly siblings?: string[];
}

export type Control =
  | HelloControl
  | ReceiptControl
  | GroupInviteControl
  | SenderKeyControl
  | GroupMessageControl
  | AttachControl
  | AttachChunkControl
  | GroupControlControl
  | EchoControl
  | CallControl
  | P2pControl
  | LinkSnapshotControl;

export function encodeControl(control: Control): string {
  return JSON.stringify(control);
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null ? { ...value } : {};
}

/** Validates (without `as`) the sender-key distribution that travels in a control. */
function asSenderKeyDistribution(value: unknown): SenderKeyDistribution | undefined {
  const r = asRecord(value);
  if (
    typeof r.groupId === "string" &&
    typeof r.senderId === "string" &&
    typeof r.chainKey === "string" &&
    typeof r.iteration === "number" &&
    typeof r.signingPub === "string"
  ) {
    return {
      groupId: r.groupId,
      senderId: r.senderId,
      chainKey: r.chainKey,
      iteration: r.iteration,
      signingPub: r.signingPub,
    };
  }
  return undefined;
}

/** Validates (without `as`) the group message (sender-keys) that travels in a control. */
function asGroupMessage(value: unknown): GroupMessage | undefined {
  const r = asRecord(value);
  if (
    typeof r.groupId === "string" &&
    typeof r.senderId === "string" &&
    typeof r.iteration === "number" &&
    typeof r.ciphertext === "string" &&
    typeof r.signature === "string"
  ) {
    return {
      groupId: r.groupId,
      senderId: r.senderId,
      iteration: r.iteration,
      ciphertext: r.ciphertext,
      signature: r.signature,
    };
  }
  return undefined;
}

function asMediaKind(value: unknown): MediaKind | undefined {
  return value === "file" || value === "voice" || value === "image" || value === "video"
    ? value
    : undefined;
}

function asAttach(r: Record<string, unknown>): AttachControl | undefined {
  const mediaKind = asMediaKind(r.mediaKind);
  if (
    typeof r.msgId !== "string" ||
    typeof r.attachId !== "string" ||
    typeof r.name !== "string" ||
    typeof r.mime !== "string" ||
    !mediaKind ||
    typeof r.size !== "number" ||
    typeof r.sha256 !== "string" ||
    typeof r.keyB64 !== "string" ||
    typeof r.chunkCount !== "number" ||
    typeof r.sentAt !== "number"
  ) {
    return undefined;
  }
  return {
    t: "attach",
    msgId: r.msgId,
    attachId: r.attachId,
    name: r.name,
    mime: r.mime,
    mediaKind,
    size: r.size,
    sha256: r.sha256,
    keyB64: r.keyB64,
    chunkCount: r.chunkCount,
    sentAt: r.sentAt,
    caption: typeof r.caption === "string" ? r.caption : undefined,
    expiresInSecs: typeof r.expiresInSecs === "number" ? r.expiresInSecs : undefined,
    durationMs: typeof r.durationMs === "number" ? r.durationMs : undefined,
    width: typeof r.width === "number" ? r.width : undefined,
    height: typeof r.height === "number" ? r.height : undefined,
  };
}

function asCallSignal(value: unknown): CallSignal | undefined {
  const r = asRecord(value);
  if (typeof r.callId !== "string") {
    return undefined;
  }
  switch (r.kind) {
    case "offer": {
      return (r.media === "audio" || r.media === "video") && typeof r.sdp === "string"
        ? { kind: "offer", callId: r.callId, media: r.media, sdp: r.sdp }
        : undefined;
    }
    case "answer": {
      return typeof r.sdp === "string"
        ? { kind: "answer", callId: r.callId, sdp: r.sdp }
        : undefined;
    }
    case "ice": {
      return typeof r.candidate === "string"
        ? { kind: "ice", callId: r.callId, candidate: r.candidate }
        : undefined;
    }
    case "hangup": {
      return {
        kind: "hangup",
        callId: r.callId,
        reason: typeof r.reason === "string" ? r.reason : undefined,
      };
    }
    default: {
      return undefined;
    }
  }
}

function asP2pSignal(value: unknown): P2pSignal | undefined {
  const r = asRecord(value);
  switch (r.kind) {
    case "want": {
      return { kind: "want" };
    }
    case "offer": {
      return typeof r.sdp === "string" ? { kind: "offer", sdp: r.sdp } : undefined;
    }
    case "answer": {
      return typeof r.sdp === "string" ? { kind: "answer", sdp: r.sdp } : undefined;
    }
    case "ice": {
      return typeof r.candidate === "string" ? { kind: "ice", candidate: r.candidate } : undefined;
    }
    case "close": {
      return { kind: "close" };
    }
    default: {
      return undefined;
    }
  }
}

function asLinkConversation(value: unknown): LinkConversation | undefined {
  const r = asRecord(value);
  if (
    typeof r.id !== "string" ||
    (r.kind !== "direct" && r.kind !== "group") ||
    typeof r.title !== "string" ||
    !Array.isArray(r.memberDeviceIds) ||
    typeof r.createdAt !== "number" ||
    typeof r.lastActivityAt !== "number"
  ) {
    return undefined;
  }
  return {
    id: r.id,
    kind: r.kind,
    title: r.title,
    peerDeviceId: typeof r.peerDeviceId === "string" ? r.peerDeviceId : undefined,
    groupId: typeof r.groupId === "string" ? r.groupId : undefined,
    memberDeviceIds: r.memberDeviceIds.filter((m): m is string => typeof m === "string"),
    createdAt: r.createdAt,
    lastActivityAt: r.lastActivityAt,
    peerIdentityDhPub: typeof r.peerIdentityDhPub === "string" ? r.peerIdentityDhPub : undefined,
    peerIdentitySigPub: typeof r.peerIdentitySigPub === "string" ? r.peerIdentitySigPub : undefined,
  };
}

function asLinkMessage(value: unknown): LinkMessage | undefined {
  const r = asRecord(value);
  if (
    typeof r.id !== "string" ||
    typeof r.conversationId !== "string" ||
    (r.direction !== "out" && r.direction !== "in") ||
    typeof r.authorDevice !== "string" ||
    typeof r.text !== "string" ||
    typeof r.sentAt !== "number"
  ) {
    return undefined;
  }
  return {
    id: r.id,
    conversationId: r.conversationId,
    direction: r.direction,
    authorDevice: r.authorDevice,
    text: r.text,
    sentAt: r.sentAt,
    expiresInSecs: typeof r.expiresInSecs === "number" ? r.expiresInSecs : undefined,
  };
}

/** Defensive parsing (it comes from the decrypted network, but could be a future version). */
export function decodeControl(body: string): Control | undefined {
  let parsed: unknown;
  try {
    parsed = JSON.parse(body);
  } catch {
    return undefined;
  }
  const r = asRecord(parsed);
  switch (r.t) {
    case "hello": {
      return typeof r.idDhPub === "string" && typeof r.idSigPub === "string"
        ? { t: "hello", idDhPub: r.idDhPub, idSigPub: r.idSigPub }
        : undefined;
    }
    case "receipt": {
      return typeof r.id === "string" ? { t: "receipt", id: r.id } : undefined;
    }
    case "group-invite": {
      const skd = asSenderKeyDistribution(r.skd);
      return typeof r.groupId === "string" &&
        typeof r.title === "string" &&
        Array.isArray(r.members) &&
        skd
        ? {
            t: "group-invite",
            groupId: r.groupId,
            title: r.title,
            members: r.members.filter((m): m is string => typeof m === "string"),
            skd,
          }
        : undefined;
    }
    case "skd": {
      const skd = asSenderKeyDistribution(r.skd);
      return typeof r.groupId === "string" && typeof r.sender === "string" && skd
        ? { t: "skd", groupId: r.groupId, sender: r.sender, skd }
        : undefined;
    }
    case "gmsg": {
      const gm = asGroupMessage(r.gm);
      return typeof r.groupId === "string" &&
        typeof r.msgId === "string" &&
        typeof r.authorDevice === "string" &&
        typeof r.sentAt === "number" &&
        gm
        ? {
            t: "gmsg",
            groupId: r.groupId,
            msgId: r.msgId,
            authorDevice: r.authorDevice,
            sentAt: r.sentAt,
            expiresInSecs: typeof r.expiresInSecs === "number" ? r.expiresInSecs : undefined,
            gm,
          }
        : undefined;
    }
    case "attach": {
      return asAttach(r);
    }
    case "attach-chunk": {
      return typeof r.attachId === "string" &&
        typeof r.index === "number" &&
        typeof r.data === "string"
        ? { t: "attach-chunk", attachId: r.attachId, index: r.index, data: r.data }
        : undefined;
    }
    case "gctl": {
      const gm = asGroupMessage(r.gm);
      return typeof r.groupId === "string" && typeof r.authorDevice === "string" && gm
        ? { t: "gctl", groupId: r.groupId, authorDevice: r.authorDevice, gm }
        : undefined;
    }
    case "echo": {
      return typeof r.peer === "string" &&
        typeof r.msgId === "string" &&
        typeof r.text === "string" &&
        typeof r.sentAt === "number"
        ? {
            t: "echo",
            peer: r.peer,
            msgId: r.msgId,
            text: r.text,
            sentAt: r.sentAt,
            expiresInSecs: typeof r.expiresInSecs === "number" ? r.expiresInSecs : undefined,
          }
        : undefined;
    }
    case "call": {
      const signal = asCallSignal(r.signal);
      return signal ? { t: "call", signal } : undefined;
    }
    case "p2p": {
      const signal = asP2pSignal(r.signal);
      return signal ? { t: "p2p", signal } : undefined;
    }
    case "link-snapshot": {
      const conversations = Array.isArray(r.conversations)
        ? r.conversations
            .map(asLinkConversation)
            .filter((c): c is LinkConversation => c !== undefined)
        : [];
      const messages = Array.isArray(r.messages)
        ? r.messages.map(asLinkMessage).filter((m): m is LinkMessage => m !== undefined)
        : [];
      const siblings = Array.isArray(r.siblings)
        ? r.siblings.filter((s): s is string => typeof s === "string")
        : undefined;
      return { t: "link-snapshot", conversations, messages, siblings };
    }
    default: {
      return undefined;
    }
  }
}
